import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface AuditLog {
  id: string;
  user_id: string | null;
  location_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  ip_address: string | null;
  performed_as: string | null;
  is_superadmin_action: boolean;
  metadata: Record<string, any>;
  created_at: string;
  // Profile data from detailed_audit_logs view
  user_first_name: string | null;
  user_last_name: string | null;
  user_role: string | null;
  performed_as_first_name: string | null;
  performed_as_last_name: string | null;
  performed_as_role: string | null;
  location_name: string | null;
  // Computed user/performed_as_user objects for backwards compatibility
  user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    role: string;
  };
  performed_as_user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    role: string;
  };
  location?: {
    id: string;
    name: string;
  };
}

export interface AuditLogFilters {
  userId?: string;
  action?: string;
  entityType?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  includeSuperadminActions?: boolean;
  limit?: number;
  offset?: number;
}

export function useAuditLogs(filters: AuditLogFilters = {}) {
  const { profile, isSuperadmin } = useAuth();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      // Use detailed_audit_logs view which includes profile data via SECURITY DEFINER function
      // This ensures profile data is accessible even when profiles are in different organizations
      let query = supabase
        .from('detailed_audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters.action) {
        query = query.eq('action', filters.action);
      }

      if (filters.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }

      if (filters.locationId) {
        query = query.eq('location_id', filters.locationId);
      }

      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      // Only superadmins can see superadmin actions
      if (!isSuperadmin || !filters.includeSuperadminActions) {
        query = query.eq('is_superadmin_action', false);
      }

      // Pagination
      const limit = filters.limit || 50;
      const offset = filters.offset || 0;
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Transform the flat view data into the expected nested structure for backwards compatibility
      const logs: AuditLog[] = (data || []).map((row: any) => ({
        ...row,
        user: row.user_id ? {
          id: row.user_id,
          first_name: row.user_first_name,
          last_name: row.user_last_name,
          role: row.user_role || 'employee',
        } : undefined,
        performed_as_user: row.performed_as ? {
          id: row.performed_as,
          first_name: row.performed_as_first_name,
          last_name: row.performed_as_last_name,
          role: row.performed_as_role || 'employee',
        } : undefined,
        location: row.location_id ? {
          id: row.location_id,
          name: row.location_name,
        } : undefined,
      }));

      return {
        logs,
        total: count || logs.length,
      };
    },
    enabled: !!profile,
  });

  // Get unique action types for filtering
  const { data: actionTypes } = useQuery({
    queryKey: ['audit-log-actions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('action')
        .order('action');

      if (error) throw error;

      // Get unique actions
      const uniqueActions = [...new Set((data as any[]).map(d => d.action))];
      return uniqueActions;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Get unique entity types for filtering
  const { data: entityTypes } = useQuery({
    queryKey: ['audit-log-entity-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('entity_type')
        .order('entity_type');

      if (error) throw error;

      const uniqueTypes = [...new Set((data as any[]).map(d => d.entity_type))];
      return uniqueTypes;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Mutation to purge audit logs (superadmin only)
  const purgeMutation = useMutation({
    mutationFn: async ({ ids, purgeAll }: { ids?: string[]; purgeAll?: boolean }) => {
      const { data, error } = await supabase.rpc('purge_audit_logs', {
        p_ids: ids || null,
        p_purge_all: purgeAll || false,
      });

      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      // Invalidate all audit log queries
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['audit-log-actions'] });
      queryClient.invalidateQueries({ queryKey: ['audit-log-entity-types'] });
    },
  });

  const purgeSelected = async (ids: string[]) => {
    return purgeMutation.mutateAsync({ ids });
  };

  const purgeAll = async () => {
    return purgeMutation.mutateAsync({ purgeAll: true });
  };

  return {
    logs: data?.logs ?? [],
    total: data?.total ?? 0,
    isLoading,
    isFetching,
    error,
    refetch,
    actionTypes: actionTypes ?? [],
    entityTypes: entityTypes ?? [],
    canViewSuperadminLogs: isSuperadmin,
    canPurge: isSuperadmin,
    purgeSelected,
    purgeAll,
    isPurging: purgeMutation.isPending,
  };
}

// Helper to format audit log action for display
export function formatAuditAction(action: string): string {
  const actionMap: Record<string, string> = {
    create: 'Created',
    update: 'Updated',
    delete: 'Deleted',
    deactivate: 'Deactivated',
    reactivate: 'Reactivated',
    update_access: 'Updated Access',
    login: 'Logged In',
    logout: 'Logged Out',
    impersonate_start: 'Started Impersonation',
    impersonate_end: 'Ended Impersonation',
    refund: 'Processed Refund',
    void: 'Voided',
    '86_item': 'Marked as 86\'d',
    un86_item: 'Unmarked 86\'d',
    designate_primary_admin: 'Designated Business Owner',
    revoke_primary_admin: 'Revoked Business Owner Status',
    demote_primary_admin: 'Demoted Business Owner',
    session_start: 'Session Started',
    session_end: 'Session Ended',
    session_heartbeat: 'Session Heartbeat',
    session_device_change: 'Session Device Change',
    session_ip_change: 'Session IP Change',
  };

  return actionMap[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Helper to format entity type for display
export function formatEntityType(entityType: string): string {
  const typeMap: Record<string, string> = {
    staff: 'Staff Member',
    order: 'Order',
    product: 'Product',
    category: 'Category',
    customer: 'Customer',
    location: 'Location',
    settings: 'Settings',
    payment: 'Payment',
    permission: 'Permission',
    register: 'Register',
    modifier: 'Modifier',
    modifier_group: 'Modifier Group',
    tax: 'Tax Rate',
    discount: 'Discount',
    gift_card: 'Gift Card',
    store_credit: 'Store Credit',
    inventory: 'Inventory',
    purchase_order: 'Purchase Order',
    supplier: 'Supplier',
    role: 'Role',
    alert_rule: 'Alert Rule',
    alert_group: 'Alert Group',
    session: 'Session',
  };

  return typeMap[entityType] || entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Human-readable field name mapping
const FIELD_NAME_MAP: Record<string, string> = {
  id: 'ID',
  user_id: 'User',
  customer_id: 'Customer',
  location_id: 'Location',
  organization_id: 'Organization',
  product_id: 'Product',
  category_id: 'Category',
  order_id: 'Order',
  order_number: 'Order Number',
  order_type: 'Order Type',
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  is_active: 'Active',
  is_offline: 'Offline Mode',
  is_public: 'Public',
  created_at: 'Created',
  updated_at: 'Updated',
  completed_at: 'Completed',
  voided_at: 'Voided',
  refunded_at: 'Refunded',
  total: 'Total',
  subtotal: 'Subtotal',
  tax_amount: 'Tax',
  tip_amount: 'Tip',
  discount_amount: 'Discount',
  guest_count: 'Guest Count',
  table_id: 'Table',
  status: 'Status',
  notes: 'Notes',
  sku: 'SKU',
  barcode: 'Barcode',
  price: 'Price',
  cost: 'Cost',
  quantity: 'Quantity',
  unit: 'Unit',
  reorder_level: 'Reorder Level',
  max_stock: 'Max Stock',
  track_inventory: 'Track Inventory',
  allow_backorder: 'Allow Backorder',
  is_modifier: 'Is Modifier',
  is_gift_card: 'Is Gift Card',
  tax_rate: 'Tax Rate',
  tax_inclusive: 'Tax Inclusive',
  kitchen_display: 'Kitchen Display',
  prep_time_minutes: 'Prep Time',
  sort_order: 'Sort Order',
  image_url: 'Image',
  description: 'Description',
  role: 'Role',
  pin: 'PIN',
  hourly_rate: 'Hourly Rate',
  overtime_rate: 'Overtime Rate',
};

// Format field name for display
export function formatFieldName(fieldName: string): string {
  if (FIELD_NAME_MAP[fieldName]) {
    return FIELD_NAME_MAP[fieldName];
  }
  // Convert snake_case to Title Case
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUrl\b/g, 'URL')
    .replace(/\bSku\b/g, 'SKU');
}

// Check if a value looks like a UUID
function isUUID(value: any): boolean {
  if (typeof value !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

// Check if a value looks like a timestamp
function isTimestamp(value: any): boolean {
  if (typeof value !== 'string') return false;
  // ISO date format
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
}

// Format a value for human-readable display
export function formatAuditValue(value: any, fieldName?: string): string {
  if (value === null || value === undefined) return '-';
  
  // Boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Timestamp values
  if (isTimestamp(value)) {
    try {
      const date = new Date(value);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(value);
    }
  }
  
  // UUID values - show truncated version
  if (isUUID(value)) {
    return value.slice(0, 8) + '...';
  }
  
  // Currency fields
  if (fieldName && ['total', 'subtotal', 'tax_amount', 'tip_amount', 'discount_amount', 'price', 'cost', 'hourly_rate', 'overtime_rate'].includes(fieldName)) {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!isNaN(num)) {
      return `$${num.toFixed(2)}`;
    }
  }
  
  // Percentage fields
  if (fieldName && ['tax_rate', 'discount_percent'].includes(fieldName)) {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!isNaN(num)) {
      return `${num}%`;
    }
  }
  
  // Status values - capitalize
  if (fieldName === 'status' && typeof value === 'string') {
    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  
  // Order type values
  if (fieldName === 'order_type' && typeof value === 'string') {
    const typeMap: Record<string, string> = {
      dine_in: 'Dine In',
      takeout: 'Takeout',
      delivery: 'Delivery',
      online: 'Online',
      kiosk: 'Kiosk',
    };
    return typeMap[value] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  
  // Role values
  if (fieldName === 'role' && typeof value === 'string') {
    const roleMap: Record<string, string> = {
      superadmin: 'Super Admin',
      primary_admin: 'Business Owner',
      owner: 'Owner',
      admin: 'Admin',
      manager: 'Manager',
      employee: 'Employee',
      cashier: 'Cashier',
    };
    return roleMap[value] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  
  // Arrays
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    if (value.length <= 3) return value.join(', ');
    return `${value.slice(0, 3).join(', ')} +${value.length - 3} more`;
  }
  
  // Objects
  if (typeof value === 'object') {
    try {
      const str = JSON.stringify(value);
      if (str.length > 50) return str.slice(0, 50) + '...';
      return str;
    } catch {
      return '[Object]';
    }
  }
  
  return String(value);
}

// Helper to get diff between old and new values with better formatting
export function getAuditDiff(
  oldValues: Record<string, any> | null,
  newValues: Record<string, any> | null
): Array<{ field: string; fieldKey: string; oldValue: any; newValue: any; formattedOld: string; formattedNew: string }> {
  const diff: Array<{ field: string; fieldKey: string; oldValue: any; newValue: any; formattedOld: string; formattedNew: string }> = [];

  if (!oldValues && !newValues) return diff;

  const allKeys = new Set([
    ...Object.keys(oldValues || {}),
    ...Object.keys(newValues || {}),
  ]);

  // Fields to skip in diff display
  const skipFields = ['id', 'created_at', 'updated_at', 'organization_id'];

  for (const key of allKeys) {
    // Skip internal fields
    if (skipFields.includes(key)) continue;

    const oldVal = oldValues?.[key];
    const newVal = newValues?.[key];

    // Skip if values are the same
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) continue;

    diff.push({
      field: formatFieldName(key),
      fieldKey: key,
      oldValue: oldVal,
      newValue: newVal,
      formattedOld: formatAuditValue(oldVal, key),
      formattedNew: formatAuditValue(newVal, key),
    });
  }

  return diff;
}

// Generate a human-readable summary of the audit log entry
export function generateAuditSummary(log: AuditLog): string {
  const userName = log.user 
    ? `${log.user.first_name || ''} ${log.user.last_name || ''}`.trim() || 'Unknown User'
    : 'System';
  
  const entityType = formatEntityType(log.entity_type);
  const action = formatAuditAction(log.action);
  
  // Get entity identifier from metadata or new_values
  let entityIdentifier = '';
  if (log.metadata?.order_number) {
    entityIdentifier = log.metadata.order_number;
  } else if (log.new_values?.order_number) {
    entityIdentifier = log.new_values.order_number;
  } else if (log.new_values?.name) {
    entityIdentifier = `"${log.new_values.name}"`;
  } else if (log.old_values?.name) {
    entityIdentifier = `"${log.old_values.name}"`;
  } else if (log.new_values?.sku) {
    entityIdentifier = `(${log.new_values.sku})`;
  } else if (log.entity_id) {
    entityIdentifier = `#${log.entity_id.slice(0, 8)}`;
  }
  
  // Build specific summaries for common actions
  switch (log.action) {
    case 'create':
      return `${userName} created ${entityType} ${entityIdentifier}`.trim();
    case 'update':
      const diff = getAuditDiff(log.old_values, log.new_values);
      if (diff.length === 1) {
        return `${userName} changed ${diff[0].field} on ${entityType} ${entityIdentifier}`.trim();
      } else if (diff.length > 1) {
        return `${userName} updated ${diff.length} fields on ${entityType} ${entityIdentifier}`.trim();
      }
      return `${userName} updated ${entityType} ${entityIdentifier}`.trim();
    case 'delete':
      return `${userName} deleted ${entityType} ${entityIdentifier}`.trim();
    case 'login':
      return `${userName} logged in`;
    case 'logout':
      return `${userName} logged out`;
    case 'session_start':
      return `${userName} started a session`;
    case 'session_end':
      return `${userName} ended a session`;
    case 'session_heartbeat':
      return `${userName} session is still active`;
    case 'session_device_change':
      return `${userName} session device changed`;
    case 'session_ip_change':
      return `${userName} session IP changed`;
    case 'void':
      return `${userName} voided ${entityType} ${entityIdentifier}`.trim();
    case 'refund':
      const refundAmount = log.new_values?.refund_amount || log.metadata?.refund_amount;
      if (refundAmount) {
        return `${userName} processed $${refundAmount.toFixed(2)} refund for ${entityType} ${entityIdentifier}`.trim();
      }
      return `${userName} processed refund for ${entityType} ${entityIdentifier}`.trim();
    case 'deactivate':
      return `${userName} deactivated ${entityType} ${entityIdentifier}`.trim();
    case 'reactivate':
      return `${userName} reactivated ${entityType} ${entityIdentifier}`.trim();
    case '86_item':
      return `${userName} marked ${entityType} ${entityIdentifier} as 86'd (out of stock)`.trim();
    case 'un86_item':
      return `${userName} removed 86'd status from ${entityType} ${entityIdentifier}`.trim();
    case 'impersonate_start':
      const targetName = log.performed_as_user 
        ? `${log.performed_as_user.first_name} ${log.performed_as_user.last_name}`.trim()
        : 'another user';
      return `${userName} started impersonating ${targetName}`;
    case 'impersonate_end':
      return `${userName} stopped impersonating`;
    case 'designate_primary_admin':
      return `${userName} designated ${entityIdentifier || 'a user'} as Business Owner`.trim();
    case 'revoke_primary_admin':
      return `${userName} revoked Business Owner status from ${entityIdentifier || 'a user'}`.trim();
    default:
      return `${userName} ${action.toLowerCase()} ${entityType} ${entityIdentifier}`.trim();
  }
}